import { isSupabaseConfigured, supabase } from '../utils/supabaseClient.js';

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function numberOrZero(value) {
  return numberOrNull(value) ?? 0;
}

export function normalizeRoleDisplay(role) {
  const normalized = String(role || '').trim();
  if (!normalized) return '';
  if (normalized.toLowerCase() === 'sr pv designer (contract)') return 'SPVD';
  return normalized;
}

function normalizeDistribution(input, labels = ['onTime', 'late']) {
  const value = input || {};
  return labels.reduce((result, key) => {
    result[key] = numberOrZero(value[key]);
    return result;
  }, {});
}

function normalizeServices(services) {
  if (!Array.isArray(services)) return [];
  return services
    .map((item) => ({
      label: String(item?.label ?? item?.service ?? item?.name ?? '').trim(),
      value: numberOrZero(item?.value ?? item?.count ?? item?.total),
    }))
    .filter((item) => item.label && item.value >= 0);
}

function normalizeAssignments(assignments) {
  if (!Array.isArray(assignments)) return [];
  return assignments.map((item, index) => ({
    id: item?.id || `${item?.project || 'assignment'}-${item?.milestone || index}-${item?.role || index}`,
    project: item?.project || item?.projectName || '',
    milestone: item?.milestone || item?.milestoneType || '',
    service: item?.service || item?.serviceType || '',
    role: normalizeRoleDisplay(item?.role || ''),
    deliveryDate: item?.deliveryDate || item?.delivery_date || '',
    tat: numberOrNull(item?.tat),
    tatUnit: item?.tatUnit || item?.tat_unit || 'hours',
    tatStatus: item?.tatStatus || item?.tat_status || '',
    qaResult: item?.qaResult || item?.qa_result || '',
    creRestampReason: item?.creRestampReason || item?.cre_restamp_reason || '',
  }));
}


function normalizeIndividualPerformance(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    topStats: Array.isArray(source.topStats) ? source.topStats : [],
    kpiSummary: Array.isArray(source.kpiSummary) ? source.kpiSummary : [],
    milestoneMix: Array.isArray(source.milestoneMix) ? source.milestoneMix : [],
    serviceMix: Array.isArray(source.serviceMix) ? source.serviceMix : [],
    roleSections: Array.isArray(source.roleSections) ? source.roleSections.map((section) => ({
      ...section,
      assignmentCount: numberOrZero(section?.assignmentCount ?? section?.assignment_count),
      metricRows: Array.isArray(section?.metricRows) ? section.metricRows : [],
      serviceRows: Array.isArray(section?.serviceRows) ? section.serviceRows : [],
    })) : [],
    idleRows: Array.isArray(source.idleRows) ? source.idleRows : [],
    assignments: Array.isArray(source.assignments) ? source.assignments : [],
  };
}

function normalizeTatPerformance(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => ({
      label: item?.label || item?.role || '',
      averageTat: numberOrNull(item?.averageTat ?? item?.average_tat),
      onTime: numberOrZero(item?.onTime ?? item?.on_time),
      late: numberOrZero(item?.late),
    })).filter((item) => item.label);
  }
  return Object.entries(value).map(([label, item]) => ({
    label,
    averageTat: numberOrNull(item?.averageTat ?? item?.average_tat),
    onTime: numberOrZero(item?.onTime ?? item?.on_time),
    late: numberOrZero(item?.late),
  }));
}

export function normalizeScoreCardPayload(payload, requestedPeriod) {
  const raw = Array.isArray(payload) ? payload[0] : payload;
  const value = raw && typeof raw === 'object' ? raw : {};
  const summary = value.summary || {};
  const employee = value.employee || {};
  const period = value.period || {};

  return {
    status: value.status || 'ok',
    message: value.message || '',
    employee: {
      id: employee.id || employee.employeeId || employee.employee_id || null,
      name: employee.name || employee.kpiTeamMemberName || employee.kpi_team_member_name || '',
      role: normalizeRoleDisplay(employee.role || ''),
      email: employee.email || '',
    },
    period: {
      start: period.start || period.startDate || requestedPeriod.startDate,
      end: period.end || period.endDate || requestedPeriod.endDate,
    },
    summary: {
      projectsDelivered: numberOrZero(summary.projectsDelivered ?? summary.projects_delivered),
      initialOnTimePct: numberOrNull(summary.initialOnTimePct ?? summary.initial_on_time_pct),
      restampOnTimePct: numberOrNull(summary.restampOnTimePct ?? summary.restamp_on_time_pct),
      internalQualityPct: numberOrNull(summary.internalQualityPct ?? summary.internal_quality_pct),
      creErrors: numberOrZero(summary.creErrors ?? summary.cre_errors),
      averageTat: numberOrNull(summary.averageTat ?? summary.average_tat),
      overallScore: numberOrNull(summary.overallScore ?? summary.overall_score),
    },
    initialDelivery: normalizeDistribution(value.initialDelivery || value.initial_delivery),
    restamp: normalizeDistribution(value.restamp),
    quality: normalizeDistribution(value.quality, ['noError', 'error']),
    errors: {
      design: numberOrZero(value.errors?.design),
      pe: numberOrZero(value.errors?.pe),
      ee: numberOrZero(value.errors?.ee),
    },
    services: normalizeServices(value.services),
    tatPerformance: normalizeTatPerformance(value.tatPerformance || value.tat_performance),
    assignments: normalizeAssignments(value.assignments),
    individualPerformance: normalizeIndividualPerformance(value.individualPerformance || value.individual_performance),
  };
}

export async function getMyScoreCard({ startDate, endDate }) {
  if (!startDate || !endDate) throw new Error('Score Card reporting dates are required.');
  if (startDate > endDate) throw new Error('Score Card start date must be on or before the end date.');

  if (!isSupabaseConfigured || !supabase) {
    return normalizeScoreCardPayload({
      status: 'integration_unavailable',
      message: 'Score Card requires the Lite Supabase integration service.',
      period: { start: startDate, end: endDate },
    }, { startDate, endDate });
  }

  const { data, error } = await supabase.functions.invoke('my-score-card', {
    body: { startDate, endDate },
  });

  if (error) {
    // Deployment/configuration errors should remain a normal in-page state in development,
    // rather than triggering Parcel's red runtime error overlay.
    console.warn('Score Card Edge Function unavailable.', { message: error.message });
    return normalizeScoreCardPayload({
      status: 'setup_required',
      message: 'Deploy the my-score-card Supabase Edge Function in the Lite project and configure its KPI Supabase secrets.',
      period: { start: startDate, end: endDate },
    }, { startDate, endDate });
  }

  return normalizeScoreCardPayload(data, { startDate, endDate });
}
